// Sphere geometry for the war map (docs/ARCHITECTURE.md §10). Unit vectors follow WarCity.unit's axis
// convention (x toward lon 0 / lat 0, y toward lon 90°E, z toward the north pole). Angles are radians of
// great-circle arc; every arc is a minor arc (strictly between 0 and π).
//
// Moved verbatim from packages/sim/src/map/sphere.ts (taxgw-2ybg A1) so the Node data tool and the sim share
// one implementation; only the error import changed to the pure subpath. sim/src/map/sphere.ts re-exports it.

import { InvariantViolation } from '@taxwar/content/geometry/invariants'

export type Vec3 = readonly [number, number, number]

export const DEG_TO_RAD = Math.PI / 180
export const RAD_TO_DEG = 180 / Math.PI

/** Signed plane-side / containment tests closer to zero than this count as "on the circle". */
const SIDE_EPSILON = 1e-12

export function unitVector(latDeg: number, lonDeg: number): Vec3 {
  const lat = latDeg * DEG_TO_RAD
  const lon = lonDeg * DEG_TO_RAD
  const cosLat = Math.cos(lat)
  return [cosLat * Math.cos(lon), cosLat * Math.sin(lon), Math.sin(lat)]
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

function negate(a: Vec3): Vec3 {
  return [-a[0], -a[1], -a[2]]
}

/** a / |a|. Signed tests on unit normals measure sin(angular distance), so SIDE_EPSILON is scale-free. */
function normalized(a: Vec3, what: string): Vec3 {
  const length = Math.sqrt(dot(a, a))
  if (!(length > 0)) throw new InvariantViolation(`${what}: degenerate (zero-length) vector`)
  return [a[0] / length, a[1] / length, a[2] / length]
}

/** atan2(|a×b|, a·b): the one angle formula, so every caller gets bit-identical results for the same inputs. */
function angleOfComponents(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const cx = ay * bz - az * by
  const cy = az * bx - ax * bz
  const cz = ax * by - ay * bx
  return Math.atan2(Math.sqrt(cx * cx + cy * cy + cz * cz), ax * bx + ay * by + az * bz)
}

/** Great-circle angle between two unit vectors. */
export function angleBetween(a: Vec3, b: Vec3): number {
  return angleOfComponents(a[0], a[1], a[2], b[0], b[1], b[2])
}

/** Great-circle angle between two points in degrees; equals angleBetween(unitVector(A), unitVector(B)) exactly. */
export function angleBetweenLatLon(latA: number, lonA: number, latB: number, lonB: number): number {
  const latARad = latA * DEG_TO_RAD
  const lonARad = lonA * DEG_TO_RAD
  const latBRad = latB * DEG_TO_RAD
  const lonBRad = lonB * DEG_TO_RAD
  const cosLatA = Math.cos(latARad)
  const cosLatB = Math.cos(latBRad)
  return angleOfComponents(
    cosLatA * Math.cos(lonARad),
    cosLatA * Math.sin(lonARad),
    Math.sin(latARad),
    cosLatB * Math.cos(lonBRad),
    cosLatB * Math.sin(lonBRad),
    Math.sin(latBRad),
  )
}

/** Midpoint of the minor arc a→b. Precondition: a and b are neither coincident nor antipodal. */
export function arcMidpoint(a: Vec3, b: Vec3): Vec3 {
  const x = a[0] + b[0]
  const y = a[1] + b[1]
  const z = a[2] + b[2]
  const length = Math.sqrt(x * x + y * y + z * z)
  if (!(length > SIDE_EPSILON)) throw new InvariantViolation('arcMidpoint: endpoints are antipodal')
  return [x / length, y / length, z / length]
}

/** x (on the great circle of arc u→v with normal n = u×v) lies strictly inside the minor arc u→v. */
function strictlyInsideArc(u: Vec3, v: Vec3, n: Vec3, x: Vec3): boolean {
  return dot(cross(u, x), n) > SIDE_EPSILON && dot(cross(x, v), n) > SIDE_EPSILON
}

function strictlyOppositeSides(sideA: number, sideB: number): boolean {
  return (sideA > SIDE_EPSILON && sideB < -SIDE_EPSILON) || (sideA < -SIDE_EPSILON && sideB > SIDE_EPSILON)
}

/**
 * True when the minor arcs a1→a2 and b1→b2 properly intersect: they cross at an interior point of both,
 * or lie on the same great circle and overlap. Precondition: the arcs share no endpoint (callers compare
 * city indices; a shared endpoint is not a crossing).
 */
export function arcsProperlyCross(a1: Vec3, a2: Vec3, b1: Vec3, b2: Vec3): boolean {
  const nA = normalized(cross(a1, a2), 'arcsProperlyCross: arc a')
  const nB = normalized(cross(b1, b2), 'arcsProperlyCross: arc b')
  const sideB1 = dot(nA, b1)
  const sideB2 = dot(nA, b2)
  if (Math.abs(sideB1) <= SIDE_EPSILON && Math.abs(sideB2) <= SIDE_EPSILON) {
    // Same great circle: overlapping arcs are a crossing.
    return (
      strictlyInsideArc(a1, a2, nA, b1) ||
      strictlyInsideArc(a1, a2, nA, b2) ||
      strictlyInsideArc(b1, b2, nB, a1) ||
      strictlyInsideArc(b1, b2, nB, a2)
    )
  }
  if (!strictlyOppositeSides(sideB1, sideB2)) return false
  if (!strictlyOppositeSides(dot(nB, a1), dot(nB, a2))) return false
  // Each arc crosses the other's great circle at exactly one of ±p; they cross iff it is the same one.
  const p = normalized(cross(nA, nB), 'arcsProperlyCross: great circles')
  const onA = strictlyInsideArc(a1, a2, nA, p)
  const onB = strictlyInsideArc(b1, b2, nB, p)
  if (onA && onB) return true
  if (onA || onB) return false
  const q = negate(p)
  return strictlyInsideArc(a1, a2, nA, q) && strictlyInsideArc(b1, b2, nB, q)
}

/**
 * Visits points along the minor arc a→b, both endpoints included, spaced by at most maxStepRad.
 * `alongRad` is the arc distance of the sample from a. Precondition: 0 < angle(a, b) < π.
 */
export function forEachArcSample(
  a: Vec3,
  b: Vec3,
  maxStepRad: number,
  visit: (latDeg: number, lonDeg: number, alongRad: number) => void,
): void {
  const theta = angleBetween(a, b)
  const sinTheta = Math.sin(theta)
  if (!(sinTheta > SIDE_EPSILON)) throw new InvariantViolation('forEachArcSample: endpoints are coincident or antipodal')
  if (!(maxStepRad > 0)) throw new InvariantViolation(`forEachArcSample: step must be positive, got ${maxStepRad}`)
  const steps = Math.max(1, Math.ceil(theta / maxStepRad))
  for (let i = 0; i <= steps; i++) {
    const along = (theta * i) / steps
    const wa = Math.sin(theta - along) / sinTheta
    const wb = Math.sin(along) / sinTheta
    const x = wa * a[0] + wb * b[0]
    const y = wa * a[1] + wb * b[1]
    const z = Math.min(1, Math.max(-1, wa * a[2] + wb * b[2]))
    visit(Math.asin(z) * RAD_TO_DEG, Math.atan2(y, x) * RAD_TO_DEG, along)
  }
}
